"use client";

import { useEffect, useState } from 'react';
import { useLocale } from 'next-intl';
import Link from 'next/link';
import { resolveImg } from '../../../lib/config';

interface Project {
  id: number;
  name: string;
  category: string;
  inheritor: string;
  description: string;
  certNo: string;
  workPhotos: string | null;
  skillDesc: string | null;
}

const PAGE_SIZE = 6;

export default function MarketplacePage() {
  const locale = useLocale();
  const isZh = locale === 'zh';
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const lang = locale === 'zh' ? 'ZH' : 'EN';
    fetch(`/api/project/public?language=${lang}`)
      .then(res => res.json())
      .then(data => {
        setAllProjects(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [locale]);

  const totalPages = Math.ceil(allProjects.length / PAGE_SIZE);
  const paginatedProjects = allProjects.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const getCategoryDisplay = (cat: string) => {
    switch (cat) {
      case 'WORLD': return isZh ? '世界级' : 'World';
      case 'NATIONAL': return isZh ? '国家级' : 'National';
      case 'PROVINCE': return isZh ? '省级' : 'Provincial';
      case 'CITY': return isZh ? '市/县级' : 'City';
      default: return cat;
    }
  };

  return (
    <div className="container" style={{ padding: '160px 20px 60px', minHeight: '60vh' }}>
      <h1 style={{ color: 'var(--un-blue-dark)', marginBottom: '30px', textAlign: 'center' }}>
        {isZh ? '亚洲非遗文化数据库' : 'Asian Intangible Cultural Heritage Database'}
      </h1>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>{isZh ? '加载中...' : 'Loading...'}</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '30px', maxWidth: '1200px', margin: '0 auto' }}>
            {paginatedProjects.map(p => (
              <Link href={`/${locale}/marketplace/${p.id}`} key={p.id} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div style={{ backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', transition: 'all 0.3s', cursor: 'pointer', border: '1px solid transparent' }}
                     onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 12px 35px rgba(0,31,63,0.12)'; }}
                     onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)'; }}>
                  
                  {/* Image */}
                  <div style={{ height: '200px', overflow: 'hidden', position: 'relative' }}>
                    {p.workPhotos ? (
                      <img src={resolveImg(p.workPhotos)} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ height: '100%', backgroundColor: 'var(--un-blue)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', fontWeight: 'bold' }}>
                        {p.name.charAt(0)}
                      </div>
                    )}
                    <span style={{ position: 'absolute', top: '12px', right: '12px', fontSize: '0.8rem', padding: '4px 12px', background: 'var(--un-gold)', color: '#fff', borderRadius: '20px', fontWeight: 600 }}>
                      {getCategoryDisplay(p.category)}
                    </span>
                  </div>

                  {/* Content */}
                  <div style={{ padding: '20px' }}>
                    <h3 style={{ margin: '0 0 8px 0', color: 'var(--un-blue-dark)', fontSize: '1.2rem' }}>{p.name}</h3>
                    <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '10px' }}>
                      <strong>{isZh ? '传承人' : 'Inheritor'}:</strong> {p.inheritor}
                    </p>
                    <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: '1.5', height: '60px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                      {p.description}
                    </p>
                    <div style={{ marginTop: '15px', paddingTop: '12px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.8rem', color: '#888' }}>
                        {isZh ? '证书编号' : 'Cert'}: {p.certNo || (isZh ? '处理中' : 'Processing')}
                      </span>
                      <span style={{ color: 'var(--un-blue)', fontSize: '0.85rem', fontWeight: 600 }}>
                        {isZh ? '查看详情 →' : 'View Details →'}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {allProjects.length === 0 && (
            <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
              {isZh ? '暂无公开的遗产项目' : 'No public heritage projects found.'}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '40px', alignItems: 'center' }}>
              <button 
                onClick={() => setPage(p => Math.max(1, p - 1))} 
                disabled={page === 1}
                style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '6px', cursor: page === 1 ? 'not-allowed' : 'pointer', background: page === 1 ? '#f5f5f5' : '#fff', color: page === 1 ? '#ccc' : '#333' }}
              >
                {isZh ? '上一页' : 'Prev'}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)}
                  style={{ padding: '8px 14px', border: p === page ? '2px solid var(--un-blue)' : '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', background: p === page ? 'var(--un-blue)' : '#fff', color: p === page ? '#fff' : '#333', fontWeight: p === page ? 700 : 400 }}
                >
                  {p}
                </button>
              ))}
              <button 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                disabled={page === totalPages}
                style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '6px', cursor: page === totalPages ? 'not-allowed' : 'pointer', background: page === totalPages ? '#f5f5f5' : '#fff', color: page === totalPages ? '#ccc' : '#333' }}
              >
                {isZh ? '下一页' : 'Next'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
